import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type { UserRef } from '../tasks/api';

/** Types of the approvals routes (docs/dev/api-tasks.md — Approvals). */

export type ApprovalType =
  | 'reservation'
  | 'property'
  | 'release'
  | 'permission'
  | 'duplicate'
  | 'gdpr'
  | 'access'
  | 'write_access'
  | 'financier_assignment';

export type ApprovalStatus = 'open' | 'approved' | 'rejected' | 'withdrawn';

export interface ApprovalRefs {
  contactId: string | null;
  dealId: string | null;
  financingId: string | null;
  propertyId: string | null;
  reservationId: string | null;
  duplicateCandidateId: string | null;
  userPermissionOverrideId: string | null;
  proposedFinancierId: string | null;
  tool: string | null;
}

export interface ApprovalItem {
  id: string;
  type: ApprovalType;
  typeLabel: string;
  status: ApprovalStatus;
  title: string;
  subtitle: string | null;
  requestedBy: UserRef | null;
  requestedAt: string;
  waitingLabel: string;
  recipient: UserRef;
  refs: ApprovalRefs;
  taskId: string | null;
  decidable: boolean;
  blockedReason: string | null;
  href: string;
}

export interface RecentApproval {
  id: string;
  type: ApprovalType;
  typeLabel: string;
  title: string;
  status: 'approved' | 'rejected' | 'withdrawn';
  decisionLabel: string;
  decidedAt: string;
  decidedLabel: string;
  href: string;
}

export interface ApprovalsResponse {
  items: ApprovalItem[];
  recent: RecentApproval[];
  counts: {
    open: number;
    decided: number;
    byType: Array<{ type: ApprovalType; label: string; count: number }>;
  };
}

export interface ApprovalDetail extends ApprovalItem {
  message: string | null;
  decidedBy: UserRef | null;
  decidedAt: string | null;
  reason: string | null;
  note: string | null;
  task: { id: string; status: 'open' | 'done' | 'closed'; title: string } | null;
  entity: {
    contact: { id: string; name: string; href: string } | null;
    deal: { id: string; number: number; label: string; href: string } | null;
    financing: { id: string; number: number; label: string; href: string } | null;
    property: { id: string; name: string; href: string } | null;
    reservation: { id: string; status: string; unitNumber: string | null; signedAt: string | null } | null;
    duplicate: {
      id: string;
      contactA: { id: string; name: string; href: string };
      contactB: { id: string; name: string; href: string };
      reason: 'phone' | 'email' | 'name';
      status: 'open' | 'merged' | 'kept_separate';
    } | null;
    proposedFinancier: UserRef | null;
    tool: string | null;
  };
}

export type ApprovalListStatus = 'open' | 'decided';

export const approvalsListKey = (status: ApprovalListStatus) =>
  [...queryRoots.approvals, 'list', status] as const;
export const approvalDetailKey = (id: string) => [...queryRoots.approvals, 'detail', id] as const;

export function useApprovals(status: ApprovalListStatus) {
  return useQuery({
    queryKey: approvalsListKey(status),
    queryFn: () => api<ApprovalsResponse>(`/approvals?status=${status}`),
  });
}

export function useApproval(id: string | null) {
  return useQuery({
    queryKey: approvalDetailKey(id ?? ''),
    queryFn: () => api<ApprovalDetail>(`/approvals/${id ?? ''}`),
    enabled: id !== null,
  });
}

function useInvalidateApprovals() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryRoots.approvals }),
      queryClient.invalidateQueries({ queryKey: queryRoots.tasks }),
      queryClient.invalidateQueries({ queryKey: queryRoots.session }),
    ]);
  };
}

export function useDecideApproval() {
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { decision: 'approved' | 'rejected'; reason?: string | null; note?: string | null };
    }) => api<ApprovalDetail>(`/approvals/${id}/decide`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useWithdrawApproval() {
  const invalidate = useInvalidateApprovals();
  return useMutation({
    mutationFn: (id: string) =>
      api<ApprovalDetail>(`/approvals/${id}/withdraw`, { method: 'POST', body: {} }),
    onSuccess: invalidate,
  });
}

/** Pill tone per type (Backoffice 09: Reservierung accent, Objekt info, Aufhebung neutral). */
export function typePillVariant(type: ApprovalType): 'accent' | 'info' | 'neutral' | 'warning' | 'danger' {
  switch (type) {
    case 'reservation':
      return 'accent';
    case 'property':
    case 'financier_assignment':
    case 'permission':
      return 'info';
    case 'duplicate':
      return 'warning';
    case 'gdpr':
      return 'danger';
    case 'release':
    case 'access':
    case 'write_access':
      return 'neutral';
  }
}

/** „freigegeben" / „abgelehnt" / „zurückgezogen" for a decided item of the list. */
export function decisionLabel(status: ApprovalStatus): string {
  switch (status) {
    case 'approved':
      return 'freigegeben';
    case 'rejected':
      return 'abgelehnt';
    case 'withdrawn':
      return 'zurückgezogen';
    case 'open':
      return 'offen';
  }
}

/** Primary decision verb per type: „Aufheben" for a release, „Freigeben" otherwise. */
export function approveLabel(type: ApprovalType): string {
  return type === 'release' ? 'Aufheben' : 'Freigeben';
}
